import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginationSchema } from '../domain/pagination';
import {
  DisplayCodeableConceptSchema,
  PaginatedBundleSchema,
  PeriodSchema,
} from '../domain/schema';

export const SupportedCareTeamStatus = [
  'proposed',
  'active',
  'suspended',
  'inactive',
  'entered-in-error',
] as const;

export const CareTeamStatusSchema = z.enum(SupportedCareTeamStatus);

export const TeamMembersSchema = z.object({
  role: z.string().array(),
  member: z.string(),
});

export const CareTeamSearchSchema = PaginationSchema.extend({
  name: z.string().optional(),
  status: CareTeamStatusSchema.optional(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional(),
});

export const ParticipantSchema = z.object({
  role: DisplayCodeableConceptSchema.array(),
  member: DisplayReferenceSchema,
});

export const CareTeamSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: DisplayReferenceSchema.array(),
  patient: DisplayReferenceSchema.optional(),
  admission: DisplayReferenceSchema.optional(),
  teamMembers: ParticipantSchema.array(),
});

export const CreateCareTeamSchema = z.object({
  name: z.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: z.string().array(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional(),
  teamMembers: TeamMembersSchema.array(),
});

export const CareTeamValidationSchema = z.object({
  name: z.string(),
  status: CareTeamStatusSchema,
  managingOrganization: z.string(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional(),
});
export const TeamMembersValidationSchema = z.object({
  role: z.string(),
  member: z.string(),
});
export const UpdateCareTeamSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  period: PeriodSchema,
  status: CareTeamStatusSchema,
  managingOrganization: z.string().array(),
  teamMembers: TeamMembersSchema.array(),
  patient: UUIDSchema.optional(),
  admission: UUIDSchema.optional(),
});

export type createBaseCareTeam = Omit<
  CreateCareTeam,
  'period' | 'teamMembers' | 'managingOrganization'
> & {
  managingOrganization: string;
};

export const CareTeamResponseSchema = CareTeamSchema.partial();
export type GetCareTeamResponse = z.infer<typeof CareTeamResponseSchema>;

export const CareTeamGetAllResponseSchema = PaginatedBundleSchema.extend({
  careTeams: CareTeamResponseSchema.array(),
});

export type CareTeamSearchParams = z.infer<typeof CareTeamSearchSchema>;

export type CareTeam = z.infer<typeof CareTeamSchema>;
export type CreateCareTeam = z.infer<typeof CreateCareTeamSchema>;

export type UpdateCareTeam = z.infer<typeof UpdateCareTeamSchema>;

export type CareTeamStatus = z.infer<typeof CareTeamStatusSchema>;
export type CareTeamMembers = z.infer<typeof ParticipantSchema>;

export type CareTeamGetAllResponse = z.infer<
  typeof CareTeamGetAllResponseSchema
>;
export type CareTeamMembersSchema = z.infer<typeof TeamMembersValidationSchema>;
